const POLLING_INTERVAL_IN_MS = 100;
const TIMEOUT_30_SECONDS_IN_MS = 30 * 1000;

const pendingKeys = new Set<string>();
const pendingWaits = new Map<string, Promise<void>>();

interface Instance {
  key: string;
  onLoad: Function;
  condition?: boolean;
}

export function waitForInstanceOnWindow(key: Instance['key']): Promise<void> {
  if (!import.meta.client) {
    return Promise.reject(
      new Error('waitForInstanceOnWindow: not available on server'),
    );
  }

  const existing = pendingWaits.get(key);

  if (existing) {
    return existing;
  }

  const promise = new Promise<void>((resolve, reject) => {
    // @ts-ignore
    if (window[key]) {
      resolve();

      return;
    }

    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      reject(
        new Error(`waitForInstanceOnWindow: timeout waiting for "${key}"`),
      );
    }, TIMEOUT_30_SECONDS_IN_MS);

    const intervalId = setInterval(() => {
      // @ts-ignore
      if (window[key]) {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        resolve();
      }
    }, POLLING_INTERVAL_IN_MS);
  });

  pendingWaits.set(key, promise);
  promise.finally(() => pendingWaits.delete(key));

  return promise;
}

export function verifyInstanceOnWindow(instance: Instance | Instance[]) {
  if (!import.meta.client || !instance) {
    return;
  }

  const normalizedInstances = !Array.isArray(instance) ? [instance] : instance;

  const deduplicatedInstances: Instance[] = [];

  for (const entry of normalizedInstances) {
    if (!pendingKeys.has(entry.key)) {
      pendingKeys.add(entry.key);
      deduplicatedInstances.push(entry);
    }
  }

  for (const { key, onLoad, condition } of deduplicatedInstances) {
    if (typeof condition === 'boolean' && !condition) {
      pendingKeys.delete(key);
      continue;
    }

    waitForInstanceOnWindow(key)
      .then(
        () => {
          onLoad();
        },
        () => {},
      )
      .finally(() => {
        pendingKeys.delete(key);
      });
  }
}
